import numbro from 'numbro';

import { exchangeRates } from '../../constants/exchangeRates';
import { Theme } from '../../constants/Theme';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import {
  AlbumBackgrounds,
  AttachmentImage,
  Balance,
  BalanceJson,
  TtsLocale,
  TtsPreferences,
  UiLocale,
  UserId,
} from '../Base';
import { RepositoryElement } from '../Repository';

export interface Email<T = number> {
  address: string;
  verificationStartedAt: T;
  verificationSentAt: T;
  isVerified: boolean;
}

interface Password<T = number> {
  isSet: boolean;
  lastModifiedAt?: T;
}

export interface MoneyJson {
  USD?: number;
  // PHP?: number;
  HKD?: number;
  // THB?: number;
  CNY?: number;
  TWD?: number;
}

export class Money {
  USD?: number;
  // PHP?: number;
  HKD?: number;
  // THB?: number;
  CNY?: number;
  TWD?: number;

  constructor(money?: MoneyJson) {
    this.USD = money?.USD ?? 0;
    this.HKD = money?.HKD ?? 0;
    this.CNY = money?.CNY ?? 0;
    this.TWD = money?.TWD ?? 0;
  }

  convertToCurrency(
    targetCurrency: string,
    minusMoney?: Money,
  ): string | undefined {
    const targetRate = exchangeRates[targetCurrency];
    if (!targetRate) {
      throw new Error(`Exchange rate for ${targetCurrency} not found`);
    }

    let convertedMoney = 0;
    Object.entries(this).forEach(([key, value]) => {
      if (value !== undefined) {
        convertedMoney =
          convertedMoney + (value / (exchangeRates[key] ?? 1)) * targetRate;
      }
    });

    let totalMinusMoney = 0;
    if (minusMoney) {
      Object.entries(minusMoney).forEach(([key, value]) => {
        if (value !== undefined) {
          totalMinusMoney =
            totalMinusMoney + (value / (exchangeRates[key] ?? 1)) * targetRate;
        }
      });
    }

    convertedMoney = convertedMoney - totalMinusMoney;

    if (convertedMoney <= 0.0) {
      return undefined;
    }
    return numbro(convertedMoney / 100).format({
      thousandSeparated: true,
      mantissa: 2,
    });
  }
}

export interface CashBalanceJson {
  potential: MoneyJson;
  confirmed: MoneyJson;
  available: MoneyJson;
}

export class CashBalance {
  potential!: Money;
  confirmed!: Money;
  available!: Money;

  constructor(cashBalance: CashBalanceJson) {
    this.potential = new Money(cashBalance.potential);
    this.confirmed = new Money(cashBalance.confirmed);
    this.available = new Money(cashBalance.available);
  }
}

enum PermissionEffect {
  Allow = 'allow',
  Deny = 'deny',
}

enum PermissionAction {
  Cma = 'cma',
  Vendor = 'vendor',
  Pet = 'pet',
}

interface Permission {
  effect: PermissionEffect;
  action: PermissionAction;
}

interface TimeManagement {
  isEnabled: boolean;
  dailyMaxUsageTime: number;
  learningZoneMaxUsageTime: number;
  learningZonePlayTime: number;
  learningZoneRestTime: number;
  petZoneMaxUsageTime: number;
  petZonePlayTime: number;
  petZoneRestTime: number;
}

interface AppSetting {
  uiTheme: Theme;
  parentalControl: boolean;
  joinNewClassrooms: boolean;
  editClassroomSettings: boolean;
  showPets: boolean;
  showAds: boolean;
  timeManagement: TimeManagement;
}

export interface DefaultGameDesign {
  backgrounds: AlbumBackgrounds;
  themeColor: string;
}

export interface AccountSettings {
  interfaceLocale: UiLocale;
  translationLocale: TtsLocale;
  app: AppSetting;
  ttsPreferences: TtsPreferences;
  gameDesign: DefaultGameDesign;
}

interface AppTimeManagementUsage<T = number> {
  lastUpdatedAt?: T;
  learningZoneUsageTime?: number;
  learningZonePlayTimeElapsed?: number;
  learningZoneRestTimeStartedAt?: T;
  petZoneUsageTime?: number;
  petZonePlayTimeElapsed?: number;
  petZoneRestTimeStartedAt?: T;
}

interface AppUsageData<T = number> {
  timeManagement?: AppTimeManagementUsage<T>;
}

export interface UsageData<T = number> {
  app?: AppUsageData<T>;
}

interface Identity {
  name: string;
  documentImage?: AttachmentImage;
}

export interface AccountJson<T = number> {
  userId: UserId;
  registeredAt: T;
  lastModifiedAt: T;
  username: string;
  email: Email<T> | null;
  userCode: string;
  password: Password<T>;
  balance: BalanceJson;
  joyCashBalance: CashBalanceJson;
  weaCashBalance: CashBalanceJson;
  permissions: Permission[];
  settings: AccountSettings;
  usageData: UsageData<T>;
  mainAccountUserId: UserId | null;
  // var buddyBoxId: Uuid?  // null
  // var joinRewardLimit: JoinRewardLimit
  // var diamondGenerator: DiamondGenerator
  lastAgreedAppTermsAt?: T;
  lastAgreedWebTermsAt?: T;
  lastLoggedInByMainAt?: T;
  willDeleteAt?: T;
  accountSuspensionStartedAt?: T;
  accountSuspensionEndedAt?: T;
  reportSuspensionStartedAt?: T;
  reportSuspensionEndedAt?: T;
  managedClassroomExpiredAt?: T;
  weaMembershipExpiredAt?: T;
  becomeInfluencerAt?: T;
  identity: Identity | null;
}

export type AccountIdentifier = 'userId';

export class Account extends RepositoryElement implements AccountJson<Date> {
  userId!: string;
  registeredAt!: Date;
  lastModifiedAt!: Date;
  username!: string;
  email!: Email<Date> | null;
  userCode!: string;
  password!: Password<Date>;
  balance: Balance;
  joyCashBalance!: CashBalance;
  weaCashBalance!: CashBalance;
  permissions!: Permission[];
  settings!: AccountSettings;
  usageData!: UsageData<Date>;
  mainAccountUserId!: UserId | null;
  lastAgreedAppTermsAt?: Date;
  lastAgreedWebTermsAt?: Date;
  lastLoggedInByMainAt?: Date;
  willDeleteAt?: Date;
  accountSuspensionStartedAt?: Date;
  accountSuspensionEndedAt?: Date;
  reportSuspensionStartedAt?: Date;
  reportSuspensionEndedAt?: Date;
  managedClassroomExpiredAt?: Date;
  weaMembershipExpiredAt?: Date;
  becomeInfluencerAt?: Date;
  identity!: Identity | null;

  constructor(account: AccountJson<Date | number>) {
    super();
    const { balance, joyCashBalance, weaCashBalance, ...rest } =
      deepTransformTimestamp(account) as AccountJson<Date>;
    this.balance = new Balance(balance);
    this.joyCashBalance = new CashBalance(joyCashBalance);
    this.weaCashBalance = new CashBalance(weaCashBalance);
    Object.assign(this, rest);
  }

  id(): string {
    return this.userId;
  }

  toJSON(): AccountJson {
    return deepTransformDate(this) as unknown as AccountJson;
  }

  get hasAnyPermission() {
    return this.permissions.some(
      (permission) => permission.effect === PermissionEffect.Allow,
    );
  }

  get hasActiveSmartParentPlan() {
    return (
      this.managedClassroomExpiredAt &&
      this.managedClassroomExpiredAt > new Date()
    );
  }

  get hasActiveWeaMembership() {
    return (
      this.weaMembershipExpiredAt && this.weaMembershipExpiredAt > new Date()
    );
  }
}
